/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useRef} from 'react';
import List from '@material-ui/core/List';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {useToggleToolTip} from '../../hooks/useToggleToolTip';
import {dlsColor} from '../../theme/theme';
import {MenuItems} from '../../types/components';
import ClickAwayListener from '../listeners/ClickAwayListener';
import Tooltip from '../tooltip/Tooltip';
import SecondaryNavigationItem from './SecondaryNavigationItem';

// defines the styles used by secondary navigation items
export const useStyles = makeStyles(theme => ({
  listWrapper : {
    background : dlsColor('grey1'),
    boxShadow  : '6px 6px 10px rgba(0, 0, 0, 0.1)',
    width      : '100%',
    maxHeight  : '370px',
    overflow   : 'auto',
    '&&'       : {
      padding : '10px 0 !important'
    }
  }
}));
// defines the props for secondary navigation item
export interface SecondaryNavigationItemsTypes {
  nestedMenuItems?: MenuItems;
  onSetPage(page: string, index: number, isMainMenu?: boolean): void;
  currentPage: string;
  secondaryNavRef?: React.MutableRefObject<any>;
};

/**
 * @SecondaryNavigationItems
 *
 * Defines all the secondary navigation items. This component should be rendered only
 * from SecondaryNavigation component with all the props mentioned below.
 *
 * ```typescript
 *
 *   <SecondaryNavigationItems onSetPage={onSetPage}
 *     nestedMenuItems={nestedMenuItems} />
 *
 * ```
 */
export const SecondaryNavigationItems = ({
  nestedMenuItems,
  onSetPage,
  currentPage,
  secondaryNavRef
}: SecondaryNavigationItemsTypes) => {
  const classes = useStyles();
  const [tooltipId, toggleToolTip] = useToggleToolTip(-1);
  const ref = useRef(null);

  const handleTooltipClick = useCallback(function() {
    toggleToolTip(-1);
  }, []);

  function CustomTooltip(props: any) {
    const index = props?.index;
    const title = translate('nav:underDevelopment.title');
    const message = translate('nav:underDevelopment.message');
    return (
      <ClickAwayListener onClickAway={handleTooltipClick}
        mountListener>
        <Tooltip title={(<div>{title}<br />{message}</div>)}
          placement='left'
          open={tooltipId === index}
          {...props}
          arrow />
      </ClickAwayListener>
    );
  }

  return (
    <>
      <List ref={secondaryNavRef} dense
        classes={{
          root : classes.listWrapper
        }}>
        {nestedMenuItems?.map((item, index) => {
          const {isPreview, label} = item;
          const NavItem = (<SecondaryNavigationItem key={label}
            ref={ref}
            item={item}
            currentPage={currentPage}
            toggleToolTip={toggleToolTip}
            handleTooltipClick={handleTooltipClick}
            index={index}
            onSetPage={onSetPage} />);

          if (!isPreview) {
            return (
              <CustomTooltip key={label}
                index={index}>
                {NavItem}
              </CustomTooltip>
            );
          }

          return NavItem;
        })}
      </List>
    </>
  );
};

export default SecondaryNavigationItems;
